// GraphComponents/StockBarGraph.tsx
import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import api from "../../../../../../utils/api";

interface StockItem {
  createdAt?: string;
  isdeleted?: boolean;
}

const fetchAllStockPages = async (page = 1, limit = 50) => {
  const params = { page, limit };
  const resp = await api.get("/api/stock", { params });
  return resp.data || resp;
};

interface BarProps {
  className?: string;
}

const StockBarGraph: React.FC<BarProps> = ({ className }) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const first = await fetchAllStockPages(1, 50);
        const total = first.total ?? 0;
        const limit = first.limit ?? 50;
        let allData: StockItem[] = Array.isArray(first.data) ? first.data : [];

        const totalPages = Math.ceil((total || allData.length) / limit) || 1;
        if (totalPages > 1) {
          const promises = [];
          for (let p = 2; p <= totalPages; p++) promises.push(fetchAllStockPages(p, limit));
          const results = await Promise.all(promises);
          results.forEach((r) => {
            if (Array.isArray(r.data)) allData = allData.concat(r.data);
          });
        }

        // Count stock per month (ignore status)
        const monthlyCounts: Record<string, number> = {};
        allData.forEach((item) => {
          const created = item.createdAt ? new Date(item.createdAt) : new Date();
          if (isNaN(created.getTime())) return;
          const month = created.toLocaleString("default", { month: "short" });
          monthlyCounts[month] = (monthlyCounts[month] || 0) + 1;
        });

        const sortedMonths = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
        const labels = sortedMonths.filter((m) => monthlyCounts[m]);
        const values = labels.map((m) => monthlyCounts[m]);

        const chartData = {
          labels,
          datasets: [
            {
              label: "Stock Quantity",
              data: values,
              backgroundColor: "hsl(207 90% 54%)",
            },
          ],
        };

        if (!cancelled) setData(chartData);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to fetch stock data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    if (chartInstance.current) chartInstance.current.destroy();

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) return;

    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data,
      options: {
        responsive: true,
        maintainAspectRatio: false, // will fill parent height
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
      },
    });

    return () => chartInstance.current?.destroy();
  }, [data]);

  if (loading) return <div className="text-center p-4 text-gray-500">Loading chart...</div>;
  if (error) return <div className="text-center p-4 text-red-500">{error}</div>;

  return <canvas ref={chartRef} style={{ width: "100%", height: "100%" }} className={className} />;
};

export default StockBarGraph;
